import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { diffLines } from 'diff';
import { AlertTriangle, Loader2, Send } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { captureHandledException } from '@/lib/sentry';
import {
  CHANGE_IMPACT, getPolicyVersion, policyKeys, publishPolicyVersion,
  type Policy, type PublishRequest, type PublishResult,
} from '@/lib/policies';
import { canonicalizeMarkdown, policyContentHash, validatePublishPayload } from '@shared/policy-core';
import { formatPolicyDate } from '@shared/policy-render';

interface PolicyPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyId: string;
  /** The published policy, or null for a first publish. */
  policy: Policy | null;
  isRequired: boolean;
  request: PublishRequest | null;
  onPublished: (result: PublishResult) => void;
}

function ContentDiff({ before, after }: { before: string; after: string }) {
  const parts = useMemo(() => diffLines(before, after), [before, after]);
  const added = parts.filter(p => p.added).reduce((n, p) => n + (p.count ?? 0), 0);
  const removed = parts.filter(p => p.removed).reduce((n, p) => n + (p.count ?? 0), 0);

  if (added === 0 && removed === 0) {
    return <p className="text-sm text-amber-700">The content is identical to the published version.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-600">
        <span className="text-green-700">+{added}</span> / <span className="text-red-700">−{removed}</span> lines
        compared with the published version
      </p>
      <pre className="max-h-64 overflow-auto rounded-md border bg-slate-50 p-3 text-xs leading-5 whitespace-pre-wrap">
        {parts.map((part, i) => {
          if (!part.added && !part.removed) {
            const lines = part.value.split('\n');
            // Collapse long unchanged runs so the edits stay visible.
            const text = lines.length > 7
              ? [...lines.slice(0, 3), `… ${lines.length - 6} unchanged lines …`, ...lines.slice(-3)].join('\n')
              : part.value;
            return <span key={i} className="text-slate-500">{text}</span>;
          }
          return (
            <span
              key={i}
              className={part.added ? 'block bg-green-100 text-green-900' : 'block bg-red-100 text-red-900 line-through'}
            >
              {part.value}
            </span>
          );
        })}
      </pre>
    </div>
  );
}

export default function PolicyPublishDialog({
  open, onOpenChange, policyId, policy, isRequired, request, onPublished,
}: PolicyPublishDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const [contentHash, setContentHash] = useState<string | null>(null);

  const currentVersion = policy?.currentVersion ?? null;
  const validation = useMemo(
    () => (request ? validatePublishPayload(request, currentVersion) : null),
    [request, currentVersion],
  );
  const change = validation?.value.bump ?? null;

  useEffect(() => {
    setConfirmed(false);
    setContentHash(null);
    if (!open || !request) return;
    let cancelled = false;
    policyContentHash(request.contentMarkdown)
      .then(hash => { if (!cancelled) setContentHash(hash); })
      .catch(error => captureHandledException(error, 'policies.publish.hash'));
    return () => { cancelled = true; };
  }, [open, request]);

  const published = useQuery({
    queryKey: policyKeys.version(policyId, currentVersion ?? ''),
    queryFn: () => getPolicyVersion(policyId, currentVersion as string),
    enabled: open && Boolean(currentVersion),
  });

  const publish = useMutation({
    mutationFn: () => publishPolicyVersion(policyId, { ...(request as PublishRequest), expectedContentHash: contentHash ?? undefined }),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all });
      toast({ title: `Version ${result.version} published`, description: 'The mobile app picks it up on next policy check.' });
      onPublished(result);
    },
    onError: (error: Error) => {
      toast({ title: 'Could not publish', description: error.message, variant: 'destructive' });
    },
  });

  if (!request || !validation) return null;
  const errors = validation.errors;
  const blocking = isRequired && (change === 'major' || change === 'initial');

  return (
    <Dialog open={open} onOpenChange={next => !publish.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Publish version {validation.value.version || request.version}</DialogTitle>
          <DialogDescription>
            Review the change before it reaches users. Published versions cannot be edited or removed.
          </DialogDescription>
        </DialogHeader>

        {errors.length > 0 ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Fix these before publishing</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5">
                {errors.map(error => <li key={error}>{error}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-5 text-sm">
            {change && (
              <Alert variant={blocking ? 'destructive' : 'default'}>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle className="flex items-center gap-2">
                  {CHANGE_IMPACT[change].label} change
                  {currentVersion && <Badge variant="outline">{currentVersion} → {validation.value.version}</Badge>}
                </AlertTitle>
                <AlertDescription>
                  {CHANGE_IMPACT[change].impact}
                  {!isRequired && change !== 'patch' && ' This policy is optional, so nothing is blocked.'}
                </AlertDescription>
              </Alert>
            )}

            <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2">
              <dt className="text-slate-500">Title</dt>
              <dd>{validation.value.title}</dd>
              <dt className="text-slate-500">Effective date</dt>
              <dd>{formatPolicyDate(validation.value.effectiveDate)}</dd>
              <dt className="text-slate-500">Content hash</dt>
              <dd className="font-mono text-xs break-all">
                {contentHash ?? <Loader2 className="h-3 w-3 animate-spin inline" />}
              </dd>
              {validation.value.summaryOfChanges.length > 0 && (
                <>
                  <dt className="text-slate-500">Summary for users</dt>
                  <dd>
                    <ul className="list-disc pl-5">
                      {validation.value.summaryOfChanges.map(item => <li key={item}>{item}</li>)}
                    </ul>
                  </dd>
                </>
              )}
            </dl>

            {currentVersion && (
              published.isLoading ? (
                <p className="text-slate-500">Loading the published version for comparison…</p>
              ) : published.data ? (
                <ContentDiff
                  before={published.data.contentMarkdown}
                  after={canonicalizeMarkdown(request.contentMarkdown)}
                />
              ) : (
                <p className="text-amber-700">The published version {currentVersion} could not be loaded for comparison.</p>
              )
            )}

            <div className="flex items-start gap-2 rounded-md border p-3">
              <Checkbox id="confirm-publish" checked={confirmed} onCheckedChange={v => setConfirmed(v === true)} />
              <Label htmlFor="confirm-publish" className="leading-5 font-normal">
                I have reviewed this version and approve publishing it
                {blocking ? '. Users will have to accept it before they can continue.' : '.'}
              </Label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={publish.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => publish.mutate()}
            disabled={errors.length > 0 || !confirmed || !contentHash || publish.isPending}
          >
            {publish.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
